import { FunctionComponent, KeyboardEvent } from 'react'
import classNames from 'classnames'
import { v4 as uuidv4 } from 'uuid'
import { ProductCard as ProductCardProps } from './ProductCard.types'
import { Label } from '../primitives/Label/Label'
import { Image } from '../primitives/Image/Image'
import { Button } from '../primitives/Button/Button'
import { FavoritesButton } from '../FavoritesButton/FavoritesButton'
import { AddToCartButton } from '../AddToCartButton/AddToCartButton'
import { Icon } from '../primitives/Icon/Icon'
import { useActions } from 'src/hooks/useActions'
import { isBundleEntityByType } from 'src/redux-api-bridge/cart/utils'
import './ProductCard.scss'

export const ProductCard: FunctionComponent<ProductCardProps> = ({
  attributes,
  data,
  index
}) => {
  const {
    chef,
    title,
    image,
    actions,
    addToCart,
    addToFavorites,
    nutritionInfo,
    premium,
    outOfStock,
    shouldUseMobileCard,
    stars,
    foodTags
  } = attributes

  const { onClick } = useActions(actions)

  const isOutOfStock = data.properties.stock === 0

  const clickAndPreventDefault = (e, fn) => {
    e?.stopPropagation?.()
    e?.preventDefault?.()

    fn()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>, fn: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      fn()
    }
  }

  const mealTags = premium || []

  return (
    <button
      id={`meal-${index}-${data.properties.inventoryId}`}
      className={classNames('core-ux-meal-card', {
        'core-ux-meal-card--sold-out': isOutOfStock,
        'core-ux-meal-card--mobile': shouldUseMobileCard
      })}
      onClick={e => clickAndPreventDefault(e, onClick)}
      onKeyDown={e => handleKeyDown(e, onClick)}
      tabIndex={0}>
      <div className="core-ux-meal-card__image-container">
        <div className="core-ux-meal-card__tags-wrapper">
          {mealTags?.map((tag, index) => (
            <Label
              key={`tag-${index}-${uuidv4()}`}
              {...tag}
              className={classNames('core-ux-meal-card__tag', {})}
            />
          ))}
        </div>

        {addToFavorites &&
          !isBundleEntityByType(data.type) &&
          !!data.properties.id && (
            <FavoritesButton
              {...addToFavorites}
              entityId={data.properties.id}
              className="core-ux-meal-card__favorites-button"
            />
          )}

        {nutritionInfo && (
          <Button
            {...nutritionInfo}
            className="core-ux-meal-card__nutrition-button"
          />
        )}

        {image && <Image {...image} className="core-ux-meal-card__image" />}
      </div>

      <div className="core-ux-meal-card__details">
        {chef && <Button {...chef} className="core-ux-meal-card__chef" />}

        <div className="core-ux-meal-card__title-container">
          {title && <Label {...title} className="core-ux-meal-card__title" />}
        </div>

        <div className="core-ux-meal-card__characteristics">
          <div className="core-ux-meal-card__rating-and-tags">
            {stars && <Label {...stars} className="core-ux-meal-card__stars" />}

            <div className="core-ux-meal-card__food-tags">
              {foodTags.map((foodTag, index) => (
                <Icon
                  key={`${index}-${uuidv4()}`}
                  {...foodTag}
                  className="core-ux-meal-card__food-tag"
                />
              ))}
            </div>
          </div>
        </div>

        {addToCart && (
          <button
            onClick={e => clickAndPreventDefault(e, () => {})}
            onKeyDown={e => handleKeyDown(e, () => {})}
            data-testid="add-to-cart-button-container"
            className="core-ux-meal-card__add-to-cart-button-container"
            tabIndex={0}>
            <AddToCartButton
              {...addToCart}
              product={data}
              index={index}
              className="core-ux-meal-card__add-to-cart"
            />
          </button>
        )}

        {isOutOfStock && (
          <div className="core-ux-meal-card__sold-out">
            {outOfStock ? <Label {...outOfStock} /> : <span>SOLD OUT</span>}
          </div>
        )}
      </div>
    </button>
  )
}
